/**
 * Preparing screen component.
 *
 * Displays a loading screen while all simulation media files are being preloaded.
 * Shows progress, current module being prepared, and loading animation.
 *
 * @module PreparingScreen
 */

import { useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { useSimulationMediaPreloader } from "@/hooks/useSimulationMediaPreloader";
import type {
  CEFRLevel,
  ExamType,
  ExamModule,
  SimulationSettings,
} from "@/types/exam-simulation";

interface PreparingScreenProps {
  readonly level: CEFRLevel;
  readonly examType: ExamType;
  readonly modules: ExamModule[];
  readonly settings: SimulationSettings | null;
  readonly userId?: string | null;
  readonly onComplete: () => void;
  readonly onError?: (error: Error) => void;
}

export function PreparingScreen({
  level,
  examType,
  modules,
  settings,
  userId,
  onComplete,
  onError,
}: PreparingScreenProps) {
  const { isLoading, isComplete, progress, error, startPreloading } =
    useSimulationMediaPreloader({
      level,
      examType,
      modules,
      settings,
      userId,
      enabled: true,
    });

  // Start preloading when component mounts
  useEffect(() => {
    startPreloading();
  }, [startPreloading]);

  // Handle completion
  useEffect(() => {
    if (isComplete && !isLoading) {
      // Small delay to show completion state
      const timer = setTimeout(() => {
        onComplete();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isComplete, isLoading, onComplete]);

  // Handle errors
  useEffect(() => {
    if (error && onError) {
      onError(error);
    }
  }, [error, onError]);

  const progressPercentage =
    progress.totalFiles > 0
      ? Math.round((progress.loadedFiles / progress.totalFiles) * 100)
      : 0;

  const getModuleDisplayName = (module: ExamModule | null): string => {
    if (!module) return "";
    const names: Record<ExamModule, string> = {
      reading: "Reading",
      listening: "Listening",
      writing: "Writing",
      speaking: "Speaking",
    };
    return names[module] || module;
  };

  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background px-4">
      <Card className="p-6 sm:p-12 max-w-lg w-full shadow-2xl shadow-primary/5 border-primary/10">
        <div className="flex flex-col items-center gap-6 sm:gap-8">
          {/* Loading Animation */}
          <div className="relative">
            <Loader2 className="w-12 h-12 sm:w-16 sm:h-16 animate-spin text-primary" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary/10 animate-pulse" />
            </div>
          </div>

          {/* Title */}
          <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold mb-2 tracking-tight">
              Preparing Simulation
            </h2>
            <p className="text-xs sm:text-base text-muted-foreground font-medium">
              Loading necessary media files...
            </p>
          </div>

          {/* Current Module */}
          {progress.currentModule && (
            <div className="text-center bg-muted/30 px-4 py-2 rounded-full border border-border/50">
              <p className="text-[10px] sm:text-xs text-muted-foreground font-bold uppercase tracking-wider">
                Current Module: {getModuleDisplayName(progress.currentModule)}
              </p>
            </div>
          )}

          {/* Progress Bar */}
          {progress.totalFiles > 0 && (
            <div className="w-full space-y-3">
              <div className="flex justify-between text-[10px] sm:text-sm text-muted-foreground font-bold uppercase tracking-tight">
                <span>
                  {progress.loadedFiles} / {progress.totalFiles} files
                </span>
                <span>{progressPercentage}%</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2 sm:h-3 overflow-hidden shadow-inner border border-border/40">
                <div
                  className="bg-primary h-full rounded-full transition-all duration-500 ease-out shadow-[0_0_12px_rgba(var(--primary),0.3)]"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="mt-4 p-4 bg-destructive/5 border border-destructive/20 rounded-xl">
              <p className="text-xs sm:text-sm text-destructive font-medium text-center">
                An error occurred while preparing the simulation. Please check
                your connection and try again.
              </p>
            </div>
          )}

          {/* Warnings for failed files */}
          {progress.failedFiles > 0 && !error && (
            <div className="mt-4 p-3 bg-amber-500/5 border border-amber-500/20 rounded-xl">
              <p className="text-[10px] sm:text-xs text-amber-700 dark:text-amber-400 font-medium text-center leading-relaxed">
                {progress.failedFiles} file
                {progress.failedFiles === 1 ? "" : "s"} failed to load. The
                simulation will continue, but some media may not be available.
              </p>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
